import { Component, Ref, Vue } from "vue-property-decorator";
import { ActionItem, fmtMsg, isGuid, MessageHelper, NotificationType } from "commonui";
import { getModule } from "vuex-module-decorators";
import { GridRef } from "@/components/wijmo-grid/grid/grid";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { UnitListLocale } from "@/locales/localeid";
import { VersionGroupModule } from "@/modules/versiongroup";
import {
	CreateUnitRequestModel,
	DisableUnitRequestModel,
	EditUnitRequestModel,
	UnitListManagerService,
	VCSLevelUnitResponseModel
} from "@/services/text-guardian/unit-list";
import { AccessLevel, CurriculumType } from "@/util/enum";
import { Button, Checkbox, FormModel, Icon, Input, InputNumber, Modal, Radio, Select, Tag } from "ant-design-vue";
import TitleListState from "@/modules/title-list";
import PageTitle from "@/components/page-title/page-title.vue";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";

@Component({
	components: {
		Radio,
		RadioGroup: Radio.Group,
		Modal,
		Form: FormModel,
		FormItem: FormModel.Item,
		Icon,
		Button,
		Tag,
		Input,
		Checkbox,
		Select,
		SelectOption: Select.Option,
		Grid,
		InputNumber,
		ActionItem,
		PageTitle
	}
})
export default class UnitList extends Vue {
	// variables
	private unitService = UnitListManagerService();
	public format = fmtMsg;
	public Locale = UnitListLocale;
	private licenseTypeValue = CurriculumType.LittleSEED;
	public littleSeed = CurriculumType.LittleSEED;
	public grapeSeed = CurriculumType.GrapeSEED;
	public createUnitModalVisible: boolean = false;
	public editUnitModalVisible: boolean = false;
	@Ref() readonly gridCompRef!: GridRef;
	public loading: boolean = false;
	public isUnitNameValid: boolean = true;
	public isUnitNumberValid: boolean = true;
	public vcsLevelUnits: VCSLevelUnitResponseModel[] = [];
	public enableSaveButton: boolean = false;
	public saveButtonLoading: boolean = false;
	public selectedGSVersion: string | undefined = "";
	public selectedLSVersion: string | undefined = "";
	public titleListModule = getModule(TitleListState);
	public accessLevel = AccessLevel;

	public labelCol = {
		span: 8
	};

	public wrapperCol = {
		span: 16
	};

	// model for createOrEditForm
	public createOrEditUnitForm = {
		id: null as any,
		displayName: "",
		unit: 0
	};

	public fieldValidity = {
		displayName: false,
		unit: false
	};

	public createUnitFormRules = {
		displayName: [
			{ required: true, message: fmtMsg(UnitListLocale.UnitListCreateNameRequired), trigger: "change" },
			{ validator: this.unitNameValidator, trigger: "change" }
		],
		unit: [
			{ required: true, message: fmtMsg(UnitListLocale.UnitListCreateUnitNumberRequired), trigger: "change" },
			{ validator: this.unitNumberValidator, trigger: "change" }
		]
	};

	public editUnitFormRules = {
		displayName: [{ validator: this.unitNameValidator, trigger: "change" }],
		unit: [{ validator: this.unitNumberValidator, trigger: "change" }]
	};

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	//Lifecycle method
	mounted() {
		const versions = VersionGroupModule?.versionGroups;
		this.selectedGSVersion =
			versions && isGuid(VersionGroupModule.selectedGsVersion)
				? versions.find((v) => v.id === VersionGroupModule?.selectedGsVersion)?.id
				: "";
		this.selectedLSVersion =
			versions && isGuid(VersionGroupModule.selectedLsVersion)
				? versions.find((v) => v.id === VersionGroupModule?.selectedLsVersion)?.id
				: "";
		const vcsLevelUnits = this.$route.meta.vcsLevelUnits;
		if (Array.isArray(vcsLevelUnits)) {
			this.vcsLevelUnits = vcsLevelUnits;
		}
	}

	// methods

	validateCreateForm(item: string, isPassed: boolean, error: string) {
		this.fieldValidity[item] = isPassed;
		let valid = true;
		for (const key in this.fieldValidity) {
			if (!this.fieldValidity[key]) {
				valid = false;
				break;
			}
		}
		this.enableSaveButton = valid;

		if (this.editUnitModalVisible && this.isUnitNameValid && this.isUnitNumberValid) {
			this.enableSaveButton = true;
		}

		this.isUnitNameValid = true;
		this.isUnitNumberValid = true;
	}

	onLicenseTypeChange(e) {
		this.licenseTypeValue = e.target.value;
	}

	showCreateUnitModal() {
		this.initModalFlags(false, true, false);

		// explicitly setting the values of form fields since after edit modal is closed
		// form.resetFields() method does not reset it to empty fields.
		this.resetFormFields();
	}

	initModalFlags(enableSaveBtn: boolean, createUnitModalVisible: boolean, editUnitModalVisible: boolean) {
		this.enableSaveButton = enableSaveBtn;
		this.createUnitModalVisible = createUnitModalVisible;
		this.editUnitModalVisible = editUnitModalVisible;
	}

	resetFormFields() {
		this.createOrEditUnitForm.displayName = "";
		this.createOrEditUnitForm.unit = 0;
		this.createOrEditUnitForm.id = 0;
	}

	showEditUnitModal() {
		this.initModalFlags(false, false, true);
	}

	onCreateUnitModalClose() {
		const form = this.$refs.createUnitForm as FormModel;
		this.createUnitModalVisible = false;
		this.editUnitModalVisible = false;
		this.saveButtonLoading = false;
		form.resetFields();
	}

	onCreateUnitModalOk() {
		const form = this.$refs.createUnitForm as FormModel;
		form.validate((valid) => {
			if (valid) {
				this.saveButtonLoading = true;
				if (this.createUnitModalVisible) {
					const createUnitRequestModel: any = { ...this.createOrEditUnitForm };
					createUnitRequestModel.curriculumType = this.licenseTypeValue;
					createUnitRequestModel.selectedGSVersion = this.selectedGSVersion;
					createUnitRequestModel.selectedLSVersion = this.selectedLSVersion;

					this.unitService.createUnit(createUnitRequestModel as CreateUnitRequestModel).then((result) => {
						if (result) {
							this.fetchVCSLevels()
								.then(() => {
									this.onCreateUnitModalClose();
									MessageHelper.Message(
										NotificationType.Success,
										fmtMsg(UnitListLocale.UnitListCreateSuccessMessage)
									);

									// this is done to refresh the vcs levels data so that always the latest data is available.
									this.titleListModule.fetchVcsLevels();
								})
								.catch((err) => {
									this.saveButtonLoading = false;
									MessageHelper.Message(
										NotificationType.Failed,
										fmtMsg(UnitListLocale.UnitListCreateFailedMessage)
									);
								});
						} else if (!result) {
							this.onCreateUnitModalClose();
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(UnitListLocale.UnitListCreateFailedMessage)
							);
						}
					});
				} else if (this.editUnitModalVisible) {
					const editUnitRequestModel: any = { ...this.createOrEditUnitForm };
					editUnitRequestModel.curriculumType = this.licenseTypeValue;
					editUnitRequestModel.selectedGSVersion = this.selectedGSVersion;
					editUnitRequestModel.selectedLSVersion = this.selectedLSVersion;

					editUnitRequestModel.disable = this.vcsLevelUnits.find(
						(u) => u.id === editUnitRequestModel.id
					)?.disable;

					this.unitService.editUnit(editUnitRequestModel as EditUnitRequestModel).then((result) => {
						if (result) {
							this.fetchVCSLevels()
								.then(() => {
									this.onCreateUnitModalClose();
									MessageHelper.Message(
										NotificationType.Success,
										fmtMsg(UnitListLocale.UnitListEditSuccessMessage)
									);

									// this is done to refresh the vcs levels data so that always the latest data is available.
									this.titleListModule.fetchVcsLevels();
								})
								.catch((err) => {
									this.saveButtonLoading = false;
									MessageHelper.Message(
										NotificationType.Failed,
										fmtMsg(UnitListLocale.UnitListEditFailedMessage)
									);
								});
						} else if (!result) {
							this.onCreateUnitModalClose();
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(UnitListLocale.UnitListEditFailedMessage)
							);
						}
					})
					.catch((err) => {
						this.saveButtonLoading = false;
					});
				}
			}
		});
	}

	unitNameValidator(rule: any, value: string, callback: (msg?: string) => void) {
		let unitNameExists = false;
		if (this.licenseTypeValue === this.grapeSeed) {
			if (this.createOrEditUnitForm.id != 0) {
				// case for edit form
				unitNameExists = this.vcsLevelUnits.some(
					(vcsLevel) =>
						vcsLevel.id != this.createOrEditUnitForm.id &&
						vcsLevel.displayName.trim().toLocaleLowerCase() === value.trim().toLocaleLowerCase() &&
						vcsLevel.curriculumType === this.grapeSeed
				);
			} else {
				unitNameExists = this.vcsLevelUnits.some(
					(vcsLevel) =>
						vcsLevel.displayName.trim().toLocaleLowerCase() === value.trim().toLocaleLowerCase() &&
						vcsLevel.curriculumType === this.grapeSeed
				);
			}
		} else if (this.licenseTypeValue === this.littleSeed) {
			if (this.createOrEditUnitForm.id != 0) {
				// case for edit form
				unitNameExists = this.vcsLevelUnits.some(
					(vcsLevel) =>
						vcsLevel.id != this.createOrEditUnitForm.id &&
						vcsLevel.displayName.trim().toLocaleLowerCase() === value.trim().toLocaleLowerCase() &&
						vcsLevel.curriculumType === this.littleSeed
				);
			} else {
				unitNameExists = this.vcsLevelUnits.some(
					(vcsLevel) =>
						vcsLevel.displayName.trim().toLocaleLowerCase() === value.trim().toLocaleLowerCase() &&
						vcsLevel.curriculumType === this.littleSeed
				);
			}
		}
		if (unitNameExists) {
			this.isUnitNameValid = false;
			callback(fmtMsg(UnitListLocale.UnitListValidationUniqueNameRequired));
			return;
		}
		if (value.length > 50) {
			callback(fmtMsg(UnitListLocale.UnitListWarningUnitNameOverflow));
			return;
		}
		callback();
	}

	validateUnitValue(value: number, callback: (msg?: string) => void) {
		if (value < 0) {
			callback(fmtMsg(UnitListLocale.UnitListWarningPositiveValue));
			return;
		} else if (value === 0) {
			callback(fmtMsg(UnitListLocale.UnitListWarningValueGreaterThanZero));
			return;
		} else if (typeof value === "string") {
			callback(fmtMsg(UnitListLocale.UnitListWarningNumericValue));
			return;
		}
	}

	unitNumberValidator(rule: any, value: number, callback: (msg?: string) => void) {
		let unitNumberExists = false;

		this.validateUnitValue(value, callback);

		if (this.licenseTypeValue === this.grapeSeed) {
			if (this.createOrEditUnitForm.id != 0) {
				//
				unitNumberExists = this.vcsLevelUnits.some(
					(vcsLevel) =>
						vcsLevel.id != this.createOrEditUnitForm.id &&
						(vcsLevel.unit === value || value <= 0) &&
						vcsLevel.curriculumType === this.grapeSeed
				);
			} else {
				unitNumberExists = this.vcsLevelUnits.some(
					(vcsLevel) => (vcsLevel.unit === value || value <= 0) && vcsLevel.curriculumType === this.grapeSeed
				);
			}
		} else if (this.licenseTypeValue === this.littleSeed) {
			if (this.createOrEditUnitForm.id != 0) {
				unitNumberExists = this.vcsLevelUnits.some(
					(vcsLevel) =>
						vcsLevel.id != this.createOrEditUnitForm.id &&
						(vcsLevel.unit === value || value <= 0) &&
						vcsLevel.curriculumType === this.littleSeed
				);
			} else {
				unitNumberExists = this.vcsLevelUnits.some(
					(vcsLevel) => (vcsLevel.unit === value || value <= 0) && vcsLevel.curriculumType === this.littleSeed
				);
			}
		}
		if (unitNumberExists) {
			this.isUnitNumberValid = false;
			callback(fmtMsg(UnitListLocale.UnitListValidationUniqueUnitNumberRequired));
			return;
		}
		callback();
	}

	get gridItemSource() {
		const items = this.vcsLevelUnits.map((unit) => ({
			...unit,
			enable: !unit.disable
		}));
		if (this.licenseTypeValue === this.littleSeed) {
			return items.filter((vcsLevel) => vcsLevel.curriculumType === this.littleSeed);
		} else {
			return items.filter((vcsLevel) => vcsLevel.curriculumType === this.grapeSeed);
		}
	}

	removeVcsLevel(levelId: number) {
		// Check if the level being removed has components associated with it.
		const vcsLevel = this.vcsLevelUnits.find((vcsLevel) => vcsLevel.id === levelId);
		if ((vcsLevel?.componentCount as number) > 0) {
			MessageHelper.Message(
				NotificationType.Warning,
				fmtMsg(UnitListLocale.UnitListDeleteWarningMessage, { componentCount: vcsLevel?.componentCount })
			);
		} else if ((vcsLevel?.objectiveCount as number) > 0) {
			MessageHelper.Message(
				NotificationType.Warning,
				fmtMsg(UnitListLocale.UnitListDeleteWarningMessageObjective)
			);
		} else {
			Modal.confirm({
				title: fmtMsg(UnitListLocale.UnitListDeleteConfirmMessage),
				okText: fmtMsg(UnitListLocale.UnitListBtnYes),
				cancelText: fmtMsg(UnitListLocale.UnitListBtnNo),
				onOk: () => {
					// If the level being removed has no components associated with it then we delete the vcsLevel.
					// Get unit associations, if any association exists with unit, need confirmation whether to delete those too.
					this.unitService
						.getUnitAssociations(levelId)
						.then((result) => {
							// If unit has R/W words associated, ask for confirmation.
							if (result && result.associatedWordCount > 0) {
								Modal.confirm({
									title: fmtMsg(UnitListLocale.UnitListWordAssociatedMsg),
									okText: fmtMsg(UnitListLocale.UnitListBtnYes),
									cancelText: fmtMsg(UnitListLocale.UnitListBtnNo),
									onOk: () => this.deleteLevel(levelId, { removeWordAssociation: true })
								});

								return;
							}

							this.deleteLevel(levelId);
						})
						.catch((err) => {
							MessageHelper.Message(
								NotificationType.Failed,
								fmtMsg(UnitListLocale.UnitListDeleteErrorMessage)
							);
						});
				}
			});
		}
	}

	deleteLevel(levelId: number, params?: any) {
		this.unitService
			.deleteLevel(levelId, params)
			.then((result) => {
				if (result) {
					MessageHelper.Message(
						NotificationType.Success,
						fmtMsg(UnitListLocale.UnitListDeleteSuccessMessage)
					);
					this.vcsLevelUnits = this.vcsLevelUnits.filter((unit) => unit.id !== levelId);
				} else if (!result) {
					MessageHelper.Message(NotificationType.Failed, fmtMsg(UnitListLocale.UnitListDeleteErrorMessage));
				}
			})
			.catch((err) => {
				MessageHelper.Message(NotificationType.Failed, fmtMsg(UnitListLocale.UnitListDeleteErrorMessage));
			});
	}

	toggleVcsLevelDisable(levelId) {
		// Check if currently the vcsLevel is enabled or disabled.
		const isEnabled = this.vcsLevelUnits.find((vcsLevel) => vcsLevel.id === levelId)?.disable as boolean;
		Modal.confirm({
			title: isEnabled
				? fmtMsg(UnitListLocale.UnitListWarningEnableUnit)
				: fmtMsg(UnitListLocale.UnitListWarningDisableUnit),
			okText: fmtMsg(UnitListLocale.UnitListBtnYes),
			cancelText: fmtMsg(UnitListLocale.UnitListBtnNo),
			onOk: () => {
				// toggles selected vcsLevel disable on front end.
				this.vcsLevelUnits = this.vcsLevelUnits.map((vcsLevel) => {
					if (vcsLevel.id === levelId) {
						vcsLevel.disable = !vcsLevel.disable;
					}
					return vcsLevel;
				});
				// service call to toggle disable on backend(db).
				const disableUnitRequestModel: DisableUnitRequestModel = { id: levelId, disable: !isEnabled };
				this.unitService.toggleDisable(disableUnitRequestModel);
			}
		});
	}

	fetchVCSLevels() {
		return this.unitService.getVCSLevelUnits().then((units) => {
			this.vcsLevelUnits = units.map((unit) => unit);
			return Promise.resolve();
		});
	}

	onDisplayNameClick(levelId) {
		// populate form fields with id of vcsLevel selected.
		const level = this.vcsLevelUnits.find((vcsLevel) => vcsLevel.id == levelId);
		this.createOrEditUnitForm.id = level?.id as number;
		this.createOrEditUnitForm.displayName = level?.displayName as string;
		this.createOrEditUnitForm.unit = level?.unit as number;

		//show the same modal with fields populated
		this.showEditUnitModal();
	}

	get getModalTitle() {
		if (this.createUnitModalVisible) {
			return fmtMsg(UnitListLocale.UnitListCreateModalTitle);
		} else if (this.editUnitModalVisible) {
			return fmtMsg(UnitListLocale.UnitListEditModalTitle);
		}
	}
}
